import axios from 'axios';
import type {
  Org,
  CommonParams,
  StoresParams,
  RegionsParams,
  SummaryResponse,
  StoresResponse,
  RegionsResponse,
  StoreDetailResponse,
} from './types';
import { mockSummary, mockStores, mockRegions, mockStoreDetail } from './mocks';

const shouldUseMocks = () => process.env.REACT_APP_USE_MOCKS === 'true';
const delay = (ms = 250) => new Promise<void>((r) => setTimeout(r, ms));

export interface RequestOpts {
  /** Which org's endpoints/fixtures to use. Defaults to SU. */
  org?: Org;
  signal?: AbortSignal;
}

/** Drops undefined/empty params so they are not sent as "param=". */
function clean<T extends object>(params: T): Partial<T> {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out as Partial<T>;
}

const base = (opts: RequestOpts) => (opts.org === 'ETH' ? '/eth/v2' : '/su/v2');

async function mockOr<T>(fn: () => T, signal?: AbortSignal): Promise<T> {
  await delay();
  if (signal?.aborted) {
    const e = new Error('canceled');
    e.name = 'CanceledError';
    throw e;
  }
  return fn();
}

export async function getSummary(params: CommonParams, opts: RequestOpts = {}): Promise<SummaryResponse> {
  if (shouldUseMocks()) return mockOr(() => mockSummary(params, opts.org), opts.signal);
  const res = await axios.get<SummaryResponse>(`${base(opts)}/summary`, { params: clean(params), signal: opts.signal });
  return res.data;
}

export async function getStores(params: StoresParams, opts: RequestOpts = {}): Promise<StoresResponse> {
  if (shouldUseMocks()) return mockOr(() => mockStores(params, opts.org), opts.signal);
  const res = await axios.get<StoresResponse>(`${base(opts)}/stores`, { params: clean(params), signal: opts.signal });
  return res.data;
}

export async function getRegions(params: RegionsParams, opts: RequestOpts = {}): Promise<RegionsResponse> {
  if (shouldUseMocks()) return mockOr(() => mockRegions(params, opts.org), opts.signal);
  const res = await axios.get<RegionsResponse>(`${base(opts)}/regions`, { params: clean(params), signal: opts.signal });
  return res.data;
}

export async function getStoreDetail(siteCode: string, opts: RequestOpts = {}): Promise<StoreDetailResponse> {
  if (shouldUseMocks()) return mockOr(() => mockStoreDetail(siteCode, opts.org), opts.signal);
  const res = await axios.get<StoreDetailResponse>(`${base(opts)}/stores/${encodeURIComponent(siteCode)}`, {
    signal: opts.signal,
  });
  return res.data;
}
